import { useEffect, useRef, useContext } from "react"
import { useDispatch } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { LOADING } from "helpers/data-statuses"
import { useIntradayMode } from "./use-intraday-mode"
import { DashboardContext } from "../context/dashboard"
import { IDashboardContextValue } from "../context/types"
import { useIsDashboardOptimizeExperimentsFeatureEnabled } from "resources/account"

/**
 * Custom hook to handle intraday warning notifications
 *
 * Intraday mode is defined as:
 * 1. Intraday feature flag enabled
 * 2. Current main dashboard instrument is an intraday-enabled portfolio
 * 3. Intraday data has been uploaded for that portfolio
 * 4. End date is intraday date (today)
 *
 * Shows an intraday data warning toast when 1, 2 are true but 3 is false (4 is false inherently)
 * Shows an optimization warning toast when 1 - 4 are true and optimization is enabled
 */
export function useIntradayWarningNotification() {
  const dispatch = useDispatch()
  const intradayDataWarningShownRef = useRef(false)
  const optimizationWarningShownRef = useRef(false)
  const { isIntraday, instrumentsMetadata } = useContext<IDashboardContextValue>(DashboardContext)
  const [{ intradayDataAvailable, isIntradayMode }, intradayBatchStatus] = useIntradayMode()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const isIntradayInstrument = !!(instrumentsMetadata?.original as { intradayEnabled?: boolean })?.intradayEnabled

  useEffect(() => {
    // Only show warning for actual intraday instruments that don't have data yet
    if (isIntradayInstrument && isIntraday && !intradayDataAvailable && intradayBatchStatus !== LOADING) {
      if (!intradayDataWarningShownRef.current) {
        intradayDataWarningShownRef.current = true
        dispatch(NotificationCreators.add("warning", null, "No intraday data. Using end of previous day."))
      }
    } else if (!isIntradayInstrument || !isIntraday || intradayDataAvailable) {
      // Reset the warning flag when:
      // - Current instrument is not intraday-enabled, OR
      // - Intraday is not enabled, OR
      // - Intraday data becomes available
      intradayDataWarningShownRef.current = false
    }
  }, [isIntradayInstrument, isIntraday, intradayDataAvailable, intradayBatchStatus, dispatch])

  useEffect(() => {
    // Only show warning for actual intraday instruments with optimization enabled
    if (isIntradayInstrument && isIntradayMode && isOptimizeExperimentsEnabled) {
      if (!optimizationWarningShownRef.current) {
        optimizationWarningShownRef.current = true
        dispatch(NotificationCreators.add("warning", null, "Optimization strategy is not available for intraday data."))
      }
    } else {
      optimizationWarningShownRef.current = false
    }
  }, [isIntradayInstrument, isIntradayMode, isOptimizeExperimentsEnabled, dispatch])
}
